import dayjs from 'dayjs';
import { makeStyles } from '@material-ui/styles';
import { Skeleton } from '@material-ui/lab';
import { MDSChip, MDSPanelBody, MDSPanelHeader, MDSTag, MDSTheme } from '@marqvision/mds/core';
import { MDSTypography } from '@marqvision/mds-v2';
import { downloadFileWithFilename } from '@marqvision/shared/src/utils';
import { ExtensionIcon } from '@marqvision/shared/src/common/ExtensionIcon';
import { useRequestDetailQuery } from '~/apis/customerRequest';
import { sliceFilename } from '../@utils';
import { requestStatusColor, requestStatusLabel, requestTypeLabel } from '../@constants';
import { DetailContentBox } from './@components/ContentBox';

const useStyles = makeStyles({
  body: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  info: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridTemplateRows: 'auto 1fr',
    gridAutoFlow: 'column dense',
    columnGap: '16px',
    rowGap: '4px',
  },
  detail: {
    padding: '16px',
    display: 'grid',
    gap: '16px',
    borderRadius: '8px',
    backgroundColor: MDSTheme.palette.bluegray[50],
  },
  processed: {
    display: 'grid',
    gap: '8px',
  },
  fileList: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
  },
});

type Props = {
  selectedId: number;
  onListMode: () => void;
};

export const RequestDetail = (props: Props) => {
  const { selectedId, onListMode } = props;
  const classes = useStyles();

  const { data, isLoading } = useRequestDetailQuery({ dbId: selectedId });

  const handleFileDownload = (url: string, name: string) => () => {
    downloadFileWithFilename(url, name);
  };

  return (
    <>
      <MDSPanelHeader title="Request" onBack={onListMode} />
        {isLoading ? (
          <MDSPanelBody className={classes.body}>
            <div className={classes.info}>
              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                ID
              </MDSTypography>
              <MDSTypography>
                <Skeleton />
              </MDSTypography>
              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                Type
              </MDSTypography>
              <MDSTypography>
                <Skeleton />
              </MDSTypography>
              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                Status
              </MDSTypography>
              <MDSTypography>
                <Skeleton />
              </MDSTypography>
            </div>

            <div className={classes.detail}>
              <MDSTypography variant="T14" weight="medium">
                Details
              </MDSTypography>

              <div>
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </div>

              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                <span>Requested </span><Skeleton width="80px" />
              </MDSTypography>
            </div>
          </MDSPanelBody>
        ) : data && (
          <MDSPanelBody className={classes.body}>
            <div className={classes.info}>
              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                ID
              </MDSTypography>
              <MDSTypography>
                {data.clientCrId}
              </MDSTypography>
              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                Type
              </MDSTypography>
              <MDSTypography>
                {requestTypeLabel[data.crType]}
              </MDSTypography>
              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                Status
              </MDSTypography>
              <MDSTypography>
                <MDSTag
                  variant="tint"
                  size="medium"
                  color={requestStatusColor[data.crStatus]}
                  label={requestStatusLabel[data.crStatus]}
                />
              </MDSTypography>
            </div>

            <div className={classes.detail}>
              <MDSTypography variant="T14" weight="medium">
                Details
              </MDSTypography>

              <DetailContentBox data={data.details} />

              {!!data.attachments.length && (
                <div className={classes.fileList}>
                  {data.attachments.map(({ s3Url, name }) => (
                    <MDSChip
                      key={s3Url}
                      variant="outline"
                      color="bluegray"
                      icon={<ExtensionIcon fileName={name} />}
                      label={sliceFilename(name)}
                      onClick={handleFileDownload(s3Url, name)}
                    />
                  ))}
                </div>
              )}

              <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                Requested {dayjs(data.requestedDate).format('MMM D, YYYY')}
              </MDSTypography>
            </div>

            {data.adminUpdatedDate && (
              <div className={classes.processed}>
                <MDSTypography weight="bold">
                  Processed
                </MDSTypography>
                <div className={classes.detail}>
                  <DetailContentBox data={data.adminResponse || ''} />

                  {!!data.adminAttachments.length && (
                    <div className={classes.fileList}>
                      {data.adminAttachments.map(({ s3Url, name }) => (
                        <MDSChip
                          key={s3Url}
                          variant="outline"
                          color="bluegray"
                          icon={<ExtensionIcon fileName={name} />}
                          label={sliceFilename(name)}
                          onClick={handleFileDownload(s3Url, name)}
                        />
                      ))}
                    </div>
                  )}

                  <MDSTypography variant="T14" color="color/content/neutral/secondary/normal">
                    Updated {dayjs(data.adminUpdatedDate).format('MMM D, YYYY')}
                  </MDSTypography>
                </div>
              </div>
            )}
          </MDSPanelBody>
        )}
    </>
  );
};